/* eslint-disable */
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { MSRemoteLoggerCategory } from 'src/app/adapters/remote-logger/utils/remote-logger.utils';
import { RemoteLoggerUseCase } from '../use-case/remote-logger/remote-logger.use-case';
import { CONSTANTS } from './config.service';
import { LoggerService } from './logger.service';
import { SqlHelperService } from './sql-helper.service';

@Injectable({
  providedIn: 'root'
})
export class PrefsService {
  db;
  table = 'preferences';
  dbParams = { name: CONSTANTS.mainFrontEndDbName, location: 'default', androidDatabaseProvider: 'system', androidLockWorkaround: 1 };
  // insert updates if it already exists
  tableSql = `CREATE TABLE IF NOT EXISTS ${this.table} (key TEXT primary key ON CONFLICT REPLACE, value TEXT)`;
  _constructed;

  constructor(
    private logger: LoggerService,
    private platform: Platform,
    private storage: Storage,
    private sqlHelper: SqlHelperService,
    private remoteLoggerUseCase: RemoteLoggerUseCase
  ) {
    this.logger.log('Prefs constructor', MSRemoteLoggerCategory.PREFS_SERVICE);
    this._constructed = this._construct();
  }

  async _construct() {
    if (CONSTANTS.USE_SERVER_DB) {
      this.logger.log('Prefs service - construct: using server db', MSRemoteLoggerCategory.PREFS_SERVICE);
      return Promise.resolve();
    } else {
      this.logger.log('Prefs service - construct: not using server db, init js db and create prefs table', MSRemoteLoggerCategory.PREFS_SERVICE);
      return this.platform.ready().then(() =>
        this.sqlHelper.createDb(this.dbParams).then((db) => {
          this.db = db;
          return this.sqlHelper.createTablePromise(this.db, this.tableSql);
        })
      );
    }
  }

  async getStorePromise(key, value) {
    await this._constructed;
    return this.sqlHelper.getInsertPromise(this.db, this.table, { key, value: JSON.stringify(value) });
  }

  _getFromDbProxyJson(value) {
    try {
      return JSON.parse(value);
    } catch (err) {
      // strings need the outer quotes, objects fail with them
      if (err instanceof SyntaxError && value.startsWith('"') && value.endsWith('"')) {
        return JSON.parse(value.substring(1, value.length - 1));
      }
    }
  }

  async getPromiseOrDefault(key, defaultValue?) {
    await this._constructed;
    const defaultVal = typeof defaultValue !== 'undefined' ? defaultValue : null;

    return new Promise((resolve, reject) => {
      this.sqlHelper
        .getSelectPromise(this.db, this.table, ['key', 'value'], { key })
        .then((rows) => {
          if (rows.length) {
            resolve(this._getFromDbProxyJson(rows[0].value));
          } else {
            this.logger.log('Did not find key in prefs:', MSRemoteLoggerCategory.PREFS_SERVICE, key, 'Returning default value:', defaultVal);
            resolve(defaultVal);
          }
        })
        .catch((err) => {
          resolve(defaultVal);
        });
    });
  }

  getPromiseOfTypeOrDefault<ValueType>(key: string, defaultValue: ValueType | null): Promise<ValueType | null> {
    return this.getPromiseOrDefault(key, defaultValue) as Promise<ValueType | null>;
  }

  async getPromise(key) {
    await this._constructed;
    return new Promise((resolve, reject) => {
      this.sqlHelper
        .getSelectPromise(this.db, this.table, ['key', 'value'], { key })
        .then((rows) => {
          if (rows.length) {
            resolve(this._getFromDbProxyJson(rows[0].value));
          } else {
            this.logger.log('Did not find key in prefs:', MSRemoteLoggerCategory.PREFS_SERVICE, key, 'Rejecting promise');
            reject();
          }
        })
        .catch((err) => {
          this.logger.log('Promise rejected in prefs for ', MSRemoteLoggerCategory.PREFS_SERVICE, key, err);
          reject(err);
        });
    });
  }

  async getPromises(...args) {
    await this._constructed;
    return this._getPromises(Array.prototype.slice.call(args));
  }

  _getPromises(keys) {
    return this.sqlHelper
      .getSelectPromise(this.db, this.table, ['key', 'value'], { key_IN: keys }, null, null, null)
      .then((rows) => {
        const keyedResults = {};
        for (const row of rows) {
          keyedResults[row.key] = this._getFromDbProxyJson(row.value);
        }
        const results = [];
        for (const key of keys) {
          results.push(key in keyedResults ? keyedResults[key] : null);
        }
        return Promise.resolve(results);
      })
      .catch((err) => {
        const results = [];
        for (const key of keys) {
          results[key] = null;
        }
        return Promise.resolve(results);
      });
  }

  async clearPrefs(): Promise<any> {
    await this._constructed;
    if (CONSTANTS.USE_SERVER_DB) {
      this.storage.clear();
    }
    // we may abort before initializing prefs
    if (this.db === undefined) {
      return Promise.resolve();
    }

    this.logger.info('Clearing prefs', MSRemoteLoggerCategory.PREFS_SERVICE);
    return this.sqlHelper.getDeletePromise(this.db, this.table, null);
  }

  getAuthParams() {
    return new Promise((resolve, reject) => {
      if (CONSTANTS.USE_SERVER_DB) {
        this.storage.get('initAuthCode').then((ac) => {
          if (ac) {
            resolve({ household_authcode: ac }); // using server keys to match server responses used in registration
          } else {
            resolve(null);
          }
        });
      } else {
        resolve(null);
      }
    });
  }

  logout() {
    return Promise.all([this.storage.remove('initAuthCode')]);
  }

  initialize(authCode, studyUri): Promise<any> {
    if (CONSTANTS.USE_SERVER_DB) {
      return this.sqlHelper
        .createDb(this.dbParams)
        .then((db) => this.sqlHelper.createTablePromise(this.db, this.tableSql))
        .then(() => Promise.all([ this.storage.set('initAuthCode', authCode) ]));
    } else {
      return Promise.resolve();
    }
  }
}
